import { Routes, Route, Navigate } from 'react-router'
import { Suspense, lazy } from 'react'
import { ProtectedRoute } from './components/auth-provider'
import { LoadingState } from './components/ui/loading'
import { ResumeOrDiscardGate } from './components/resume-or-discard-gate'
import { RootAccountsRedirect } from './components/root-accounts-redirect'
import { isApplicationListEnabled } from './lib/approval/feature-flags'

/**
 * Route map — mirrors app/ directory structure from Next.js.
 * Each page is lazy-loaded for code splitting.
 *
 * Migration status:
 *   [x] /                    → redirects to /accounts
 *   [x] /apply               → app/apply/page.tsx
 *   [x] /apply/submitted     → app/apply/submitted/page.tsx
 *   [x] /apply/express       → app/apply/express/page.tsx
 *   [x] /accounts            → app/accounts/page.tsx
 *   [x] /account             → app/account/page.tsx
 *   [x] /account/portal      → app/account/portal/page.tsx
 *   [x] /login               → app/login/page.tsx
 *   [x] /impact              → app/impact/page.tsx
 *   [x] /demo                → app/demo/page.tsx (DemoPage)
 *   [x] /demo/pride          → app/demo/pride/page.tsx (PridePage)
 *   [x] /demo/regeneration   → app/demo/regeneration/page.tsx (RegenerationPage)
 *   [x] /privacy             → app/privacy/page.tsx
 *   [x] /terms               → app/terms/page.tsx
 *   [x] /embedded-persona    → app/embedded-persona/page.tsx
 */

// Lazily import pages
const ApplyPage           = lazy(() => import('./pages/apply/ApplyPage'))
const ApplySubmittedPage  = lazy(() => import('./pages/apply/SubmittedPage'))
const ApplyExpressPage    = lazy(() => import('./pages/apply/ExpressPage'))
const DonationChoicePage  = lazy(() => import('./pages/apply/DonationChoicePage'))
const AccountsPage        = lazy(() => import('./pages/AccountsPage'))
const BusinessAccountsPage = lazy(() => import('./pages/BusinessAccountsPage'))
const BusinessApplyPage   = lazy(() => import('./pages/BusinessApplyPage'))
const AccountPortalPage   = lazy(() => import('./pages/account/PortalPage'))
const LoginPage           = lazy(() => import('./pages/LoginPage'))
const ImpactPage          = lazy(() => import('./pages/ImpactPage'))
const DemoPage            = lazy(() => import('./pages/demo/DemoPage'))
const PridePage           = lazy(() => import('./pages/demo/PridePage'))
const RegenerationPage   = lazy(() => import('./pages/demo/RegenerationPage'))
const PrivacyPage         = lazy(() => import('./pages/PrivacyPage'))
const TermsPage           = lazy(() => import('./pages/TermsPage'))
const EmbeddedPersonaPage = lazy(() => import('./pages/EmbeddedPersonaPage'))
const ApplicationsPage = lazy(() => import('./pages/approval/ApplicationsPage'))
const ReviewPage = lazy(() => import('./pages/approval/ReviewPage'))
const AdminProductsListPage = lazy(() => import('./pages/admin/ProductsListPage'))
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'))
const DonationTargetsPage = lazy(() => import('./pages/admin/DonationTargetsPage'))
const OfficerCodesPage = lazy(() => import('./pages/admin/OfficerCodesPage'))
const UrlGeneratorPage = lazy(() => import('./pages/admin/UrlGeneratorPage'))
const NotFoundPage       = lazy(() => import('./pages/NotFoundPage'))

export default function App() {
  const applicationListEnabled = isApplicationListEnabled()

  return (
    <Suspense fallback={<LoadingState variant="page" />}>
      <Routes>
        <Route path="/"                  element={<RootAccountsRedirect />} />
        <Route
          path="/apply"
          element={
            <ResumeOrDiscardGate platform="consumer" redirectPath="/apply">
              <ApplyPage />
            </ResumeOrDiscardGate>
          }
        />
        <Route path="/apply/submitted"   element={<ApplySubmittedPage />} />
        <Route path="/apply/donation-choice" element={<DonationChoicePage />} />
        <Route path="/apply/express"     element={<ApplyExpressPage />} />
        <Route path="/accounts"          element={<AccountsPage />} />
        <Route path="/business/accounts" element={<BusinessAccountsPage />} />
        <Route
          path="/business/apply"
          element={
            <ResumeOrDiscardGate platform="business" redirectPath="/business/apply">
              <BusinessApplyPage />
            </ResumeOrDiscardGate>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountPortalPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/portal"
          element={
            <ProtectedRoute>
              <AccountPortalPage />
            </ProtectedRoute>
          }
        />
        <Route path="/login"             element={<LoginPage />} />
        <Route path="/impact"            element={<ImpactPage />} />
        <Route path="/demo"              element={<DemoPage />} />
        <Route path="/demo/pride"        element={<PridePage />} />
        <Route path="/demo/regeneration" element={<RegenerationPage />} />
        <Route path="/privacy"           element={<PrivacyPage />} />
        <Route path="/terms"             element={<TermsPage />} />
        <Route path="/embedded-persona"  element={<EmbeddedPersonaPage />} />
        <Route
          path="/approval/applications"
          element={applicationListEnabled ? <ApplicationsPage /> : <NotFoundPage />}
        />
        <Route path="/approval/applications/:platform/:applicationId" element={<ReviewPage />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="products" replace />} />
          <Route path="products" element={<AdminProductsListPage />} />
          <Route path="donation-targets" element={<DonationTargetsPage />} />
          <Route path="officer-codes" element={<OfficerCodesPage />} />
          <Route path="url-generator" element={<UrlGeneratorPage />} />
        </Route>
        <Route path="*"                  element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}
